import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, MapPin, ShieldCheck, Folder } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";

export default function EventDetail() {
  const { slug } = useParams();
  const [data, setData] = useState({ event: null, subfolders: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    customerApi.get(`/public/events/${slug}`)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  const e = data.event;

  return (
    <div data-testid="page-event-detail" className="pt-32 pb-24">
      <SEO title="Event Gallery — WelkomBiz Photography" description="Browse the gallery for this WelkomBiz Photography event. Watermarked previews — unlock full-quality with credits." />
      {/* Hero */}
      <section className="relative max-w-[1280px] mx-auto px-5 sm:px-8 mb-12">
        <Link to="/events" data-testid="event-back" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Events
        </Link>
        {loading || !e ? <p className="mt-6 text-sm text-white/40">Loading…</p> : (
          <>
            <p className="mt-5 text-[10px] tracking-luxe uppercase text-white/45">— {e.category}</p>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] max-w-4xl">
              <span className="text-chrome">{e.title}</span>
            </h1>
            <div className="mt-4 flex flex-wrap gap-4 text-[11px] tracking-luxe uppercase text-white/55">
              <span className="inline-flex items-center gap-2"><Calendar className="w-3 h-3" /> {e.date}</span>
              {e.location && <span className="inline-flex items-center gap-2"><MapPin className="w-3 h-3" /> {e.location}</span>}
              <span>{e.subfolder_count} folders · {e.photo_count} photos</span>
            </div>
            {(e.short_description || e.full_description) && (
              <p className="mt-6 text-white/65 font-body max-w-3xl">
                {e.full_description || e.short_description}
              </p>
            )}
            {e.cover_image && (
              <div className="photo-frame chrome-border mt-8 w-full h-[260px] sm:h-[420px] lg:h-[520px]">
                <img src={e.cover_image} alt={e.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
          </>
        )}
      </section>

      {/* Watermark notice */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 mb-8">
        <div className="glass chrome-border rounded-md p-4 sm:p-5 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-[#ff2e93] mt-0.5 shrink-0" />
          <p className="text-xs sm:text-sm text-white/65 font-body">
            All photos shown are <strong>watermarked previews</strong>. Full-quality downloads will be available
            once credits are activated — assigned by WelkomBiz Photography after payment.
          </p>
        </div>
      </section>

      {/* Pricing */}
      {data.pricing && data.pricing.status === "active" && (
        <section data-testid="customer-pricing" className="max-w-[1280px] mx-auto px-5 sm:px-8 mb-10">
          <h2 className="font-display text-2xl sm:text-3xl text-chrome mb-4">Credit Packages</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              {n:1, k:"single_photo_credits"},
              {n:5, k:"five_photo_credits"},
              {n:10, k:"ten_photo_credits"},
              {n:15, k:"fifteen_photo_credits"},
              {n:20, k:"twenty_photo_credits"},
            ].map(({n,k}) => (
              <div key={n} className="glass chrome-border rounded-md p-4 text-center">
                <p className="font-display text-3xl text-chrome">{n}</p>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1">Photo{n>1?"s":""}</p>
                <p className="font-display text-xl text-magcyan mt-3">{data.pricing[k]} cr</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] tracking-luxe uppercase text-white/45 mt-3">
            Package specials will be automatically applied during checkout in the next phase.
          </p>
        </section>
      )}

      {/* Subfolders */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <h2 className="font-display text-2xl sm:text-3xl text-chrome mb-6">Gallery Folders</h2>
        {loading ? <p className="text-sm text-white/40">Loading…</p> :
         data.subfolders.length === 0 ? (
          <div className="glass chrome-border rounded-md p-10 text-center text-white/55">
            This event’s gallery is being prepared. Folders will appear here once published.
          </div>
         ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.subfolders.map((f) => (
              <Link key={f.id} to={`/events/${slug}/${f.id}`} data-testid={`subfolder-card-${f.id}`} className="group photo-frame chrome-border aspect-[4/3] block overflow-hidden">
                {f.cover_thumbnail_url
                  ? <img src={f.cover_thumbnail_url} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
                  : <div className="w-full h-full bg-black/60 flex items-center justify-center text-white/25"><Folder className="w-7 h-7" /></div>}
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.95))" }} />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 className="font-display text-2xl text-chrome">{f.name}</h3>
                  <p className="mt-1 text-[10px] tracking-luxe uppercase text-white/55">{f.photo_count ?? 0} photos</p>
                  {f.description && <p className="text-xs text-white/55 mt-2 line-clamp-2 max-w-[34ch]">{f.description}</p>}
                </div>
              </Link>
            ))}
          </div>
         )
        }
      </section>
    </div>
  );
}
